import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { useStore } from './store/useStore';
import { count, flush } from './utils/offlineQueue';
import api from './utils/api';
import toast from 'react-hot-toast';

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);

// ── PWA service worker ────────────────────────────────────────────────────────
// Only active in production builds (or when REACT_APP_SW_DEV=true in .env.local)
serviceWorkerRegistration.register({
  onUpdate: () => {
    // The new SW already called skipWaiting() + clientsClaim(), so the
    // page is about to reload (handled by the controllerchange listener
    // in serviceWorkerRegistration). Brief toast for visibility — the
    // reload will fire within ~500ms and the toast vanishes with it.
    toast('Updating to the new version…', {
      icon: '🔄',
      id: 'sw-updating', // singleton — don't stack on rapid deploys
      duration: 4000,
    });
  },
});

// ── Connectivity listeners ────────────────────────────────────────────────────
window.addEventListener('offline', () => {
  useStore.getState().setIsOnline(false);
  toast('You\'re offline — changes will sync when you reconnect', {
    icon: '📡',
    id: 'offline-toast', // singleton so it doesn't stack
    duration: 5000,
  });
});

window.addEventListener('online', async () => {
  useStore.getState().setIsOnline(true);

  try {
    const pending = await count();

    if (pending === 0) {
      toast.success('Back online', { duration: 2000 });
      return;
    }

    const toastId = toast.loading(
      `Syncing ${pending} pending change${pending > 1 ? 's' : ''}…`
    );

    const { synced, failed } = await flush(api, {
      onIdRemap: (tempId, realId) => {
        // Replace the client-side temp ID with the real server ID in the store
        useStore.getState().updateMarkup(tempId, { id: realId, _offline: undefined });
      },
    });

    toast.dismiss(toastId);

    if (synced > 0) {
      toast.success(`${synced} change${synced > 1 ? 's' : ''} synced`);
    }
    if (failed > 0) {
      toast.error(
        `${failed} change${failed > 1 ? 's' : ''} failed to sync — will retry`
      );
    }
  } catch (err) {
    console.error('[Sync] Failed to flush offline queue:', err);
  }
});
